import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Container,
  Badge,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiDownload, FiRefreshCw, FiFilter } from "react-icons/fi";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getAssetLifecycleReport } from "../../services/ReportServices";
import {
  searchcategoryOptionApi,
  searchSubcategoryOptionApi,
} from "../../services/SearchService";
import { FiChevronDown, FiChevronRight } from "react-icons/fi";
import { FaArrowRight, FaArrowDownLong } from "react-icons/fa6";
import {
  FaUserCheck,
  FaUndo,
  FaExchangeAlt,
  FaTools,
  FaTrash,
} from "react-icons/fa";

const AssetLifecycleHistoryReport = () => {
  // Date range options
  const dateRangeOptions = [
    { label: "All", value: "all" },
    { label: "Yesterday", value: "yesterday" },
    { label: "Last 7 Days", value: "7_days" },
    { label: "Last 15 Days", value: "15_days" },
    { label: "Last 30 Days", value: "30_days" },
    { label: "Last Month", value: "last_month" },
    { label: "Last 3 Months", value: "3_months" },
    { label: "Custom", value: "custom" },
  ];

  // State for data
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // State for dropdowns
  const [dropdownCategory, setDropdownCategory] = useState(null);
  const [dropdownSubCategory, setDropdownSubCategory] = useState(null);
  const [dropdownCategoryOption, setDropdownCategoryOption] = useState([]);
  const [dropdownSubCategoryOption, setDropdownSubCategoryOption] = useState(
    []
  );
  const [filteredSubcategoryOptions, setFilteredSubCategoryOptions] = useState(
    []
  );

  const [selectedDateRange, setSelectedDateRange] = useState(
    dateRangeOptions[4]
  );
  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRow = (index) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  // const formatEventType = (type) => {
  //   if (!type) return "-";
  //   return type
  //     .toLowerCase()
  //     .split("_")
  //     .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  //     .join(" ");
  // };

  const getEventIcon = (type) => {
    switch (type?.toUpperCase()) {
      case "ASSIGNED":
        return <FaUserCheck />;
      case "RETURNED":
        return <FaUndo />;
      case "MOVED":
        return <FaExchangeAlt />;
      case "MAINTENANCE":
        return <FaTools />;
      case "DISPOSED":
        return <FaTrash />;
      default:
        return null;
    }
  };
  const getEventBadge = (type) => {
    switch (type?.toUpperCase()) {
      case "ASSIGNED":
        return "success";
      case "RETURNED":
        return "primary";
      case "MOVED":
        return "info";
      case "MAINTENANCE":
        return "warning";
      case "DISPOSED":
        return "danger";
      default:
        return "secondary";
    }
  };
  const formatEventType = (type) => {
    if (!type) return "-";
    switch (type.toUpperCase()) {
      case "ASSIGNED":
        return "Assigned";
      case "RETURNED":
        return "Returned";
      case "MOVED":
        return "Moved";
      case "MAINTENANCE":
        return "Maintenance";
      case "DISPOSED":
        return "Disposed";
      default:
        return type;
    }
  };

  // State for filters
  const [param, setParam] = useState({
    page: 1,
    page_size: 10,
    query: "",
    category_id: "",
    subcategory_id: "",
    date_range: "all",
    start_date: "",
    end_date: "",
  });

  // Format date for display
  const formatDate = (date) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0]; // returns 'YYYY-MM-DD'
  };

  // Initialize component
  useEffect(() => {
    // Calculate initial dates for weekly period
    const today = new Date();
    const start_date = formatDate(today.setDate(today.getDate() - 7));
    const end_date = formatDate(new Date());

    setParam((prev) => ({
      ...prev,
      start_date,
      end_date,
    }));

    // Load categories
    searchcategoryOptionApi()
      .then((res) => {
        const categoryOpts =
          res?.data?.results?.map((item) => ({
            label: item.name,
            value: item.id,
            subcategories: item.subcategories || [],
          })) || [];

        setDropdownCategoryOption(categoryOpts);

        const allSubCats = categoryOpts.flatMap((cat) =>
          cat.subcategories.map((sub) => ({
            label: sub.name,
            value: sub.id,
            categoryId: cat.value,
          }))
        );

        setDropdownSubCategoryOption(allSubCats);
        setFilteredSubCategoryOptions(allSubCats);
      })
      .catch(() => toast.error("Failed to load categories"));
  }, []);

  // Handle date range selection
  useEffect(() => {
    if (selectedDateRange.value === "custom") return;

    const today = new Date();
    let startDate = "";
    let endDate = "";

    switch (selectedDateRange.value) {
      case "yesterday":
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = formatDate(yesterday);
        endDate = formatDate(yesterday);
        break;
      case "7_days":
        startDate = formatDate(new Date(today.setDate(today.getDate() - 7)));
        endDate = formatDate(new Date());
        break;
      case "15_days":
      case "30_days":
      case "3_months":
        const days =
          selectedDateRange.value === "15_days"
            ? 15
            : selectedDateRange.value === "30_days"
            ? 30
            : 90;
        startDate = formatDate(new Date(today.setDate(today.getDate() - days)));
        endDate = formatDate(new Date());
        break;
      case "last_month":
        const firstDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1
        );
        const lastDayLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0
        );
        startDate = formatDate(firstDayLastMonth);
        endDate = formatDate(lastDayLastMonth);
        break;
      default:
        // "all" or others - no date range
        startDate = "";
        endDate = "";
    }

    setParam((prev) => ({
      ...prev,
      date_range: selectedDateRange.value,
      start_date: startDate,
      end_date: endDate,
      page: 1,
    }));
  }, [selectedDateRange]);

  // Fetch data when filters change
  useEffect(() => {
    const delay = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(delay);
  }, [param]);

  // Fetch report data
  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Validate custom date range
      if (
        param.date_range === "custom" &&
        (!param.start_date || !param.end_date)
      ) {
        throw new Error(
          "Please select both start and end dates for custom range"
        );
      }

      if (param.date_range === "custom") {
        const start = new Date(param.start_date);
        const end = new Date(param.end_date);

        if (start > end) {
          throw new Error("Start date cannot be after end date");
        }
      }

      const response = await getAssetLifecycleReport({
        ...param,
        export: false,
      });

      if (!response.data) {
        throw new Error("Invalid response format from server");
      }

      setReportData(response.data.results || []);
      setTotalCount(response.data.count || 0);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch asset lifecycle history";
      setError(errorMessage);
      toast.error(errorMessage);
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Handle category change
  const handleCategoryChange = (selected) => {
    setDropdownCategory(selected);
    setDropdownSubCategory(null);

    const filtered = selected
      ? dropdownSubCategoryOption.filter(
          (sub) => sub.categoryId === selected.value
        )
      : dropdownSubCategoryOption;

    setFilteredSubCategoryOptions(filtered);
    setParam((prev) => ({
      ...prev,
      category_id: selected?.value || "",
      subcategory_id: "",
      page: 1,
    }));
  };

  // Handle subcategory change
  const handleSubCategoryChange = (selected) => {
    setDropdownSubCategory(selected);
    const relatedCat = dropdownCategoryOption.find(
      (cat) => cat.value === selected?.categoryId
    );

    if (
      relatedCat &&
      (!dropdownCategory || relatedCat.value !== dropdownCategory.value)
    ) {
      setDropdownCategory(relatedCat);
      setFilteredSubCategoryOptions(
        dropdownSubCategoryOption.filter(
          (sub) => sub.categoryId === relatedCat.value
        )
      );
    }

    setParam((prev) => ({
      ...prev,
      category_id: relatedCat?.value || "",
      subcategory_id: selected?.value || "",
      page: 1,
    }));
  };

  // Handle search change
  const handleSearchChange = (value) => {
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  // Handle page change
  const handlePageChange = (selectedPage) => {
    setParam((prev) => ({
      ...prev,
      page: selectedPage.selected + 1,
    }));
  };

  // Handle export
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await getAssetLifecycleReport({
        ...param,
        export: true,
      });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `asset_lifecycle_history_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export started successfully");
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  // Get badge color based on event type
  // const getEventBadge = (logType) => {
  //   switch (logType) {
  //     case "allocation":
  //       return "primary";
  //     case "return":
  //       return "success";
  //     case "movement":
  //       return "warning";
  //     case "maintenance":
  //       return "info";
  //     case "disposal":
  //       return "danger";
  //     default:
  //       return "secondary";
  //   }
  // };

  // Render event details based on type
  const renderEventDetails = (log) => {
    switch (log.log_type) {
      case "allocation":
        return (
          log.allocation_log && (
            <div>
              <strong>Type:</strong>{" "}
              {log.allocation_log.allocation_type_display}
              <br />
              {log.allocation_log.to_employee && (
                <span>
                  <strong>Employee:</strong>{" "}
                  {log.allocation_log.to_employee.fullname}
                </span>
              )}
              {log.allocation_log.to_department && (
                <span>
                  <strong>Department:</strong>{" "}
                  {log.allocation_log.to_department.name}
                </span>
              )}
              {log.allocation_log.to_area && (
                <span>
                  <strong>Area:</strong> {log.allocation_log.to_area.name}
                </span>
              )}
            </div>
          )
        );
      case "return":
        return (
          log.return_log && (
            <div>
              <strong>Status:</strong> {log.return_log.return_status_display}
            </div>
          )
        );
      case "movement":
        return (
          log.movement_log && (
            <div>
              <strong>Status:</strong>{" "}
              {log.movement_log.movement_status_display}
            </div>
          )
        );
      case "maintenance":
        return (
          log.maintenance_log && (
            <div>
              <strong>Type:</strong>{" "}
              {log.maintenance_log.maintenance_type_display}
            </div>
          )
        );
      case "disposal":
        return (
          log.disposal_log && (
            <div>
              <strong>Reason:</strong> {log.disposal_log.disposal_reason}
            </div>
          )
        );
      default:
        return "-";
    }
  };

  return (
    <Container fluid>
      {/* Breadcrumb and header */}
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div>
          <h2 className="text-primary mb-0">Asset Lifecycle History Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active">Asset Lifecycle History</li>
          </ol>
        </div>

        <div
          className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
          style={{ zIndex: 10 }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card style={{ minHeight: "350px" }}>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            <FiFilter className="me-2" /> Asset Lifecycle Events
          </h5>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={param.query}
              handleSearchChange={handleSearchChange}
            />

            <GlobalSearchableDropdown
              options={dropdownCategoryOption}
              selected={dropdownCategory}
              onSelect={handleCategoryChange}
              placeholder="Category"
              width="180px"
            />

            <GlobalSearchableDropdown
              options={filteredSubcategoryOptions}
              selected={dropdownSubCategory}
              onSelect={handleSubCategoryChange}
              placeholder="Sub-Category"
              width="180px"
            />

            <GlobalSearchableDropdown
              options={dateRangeOptions}
              selected={selectedDateRange}
              onSelect={(selected) =>
                setSelectedDateRange(selected || dateRangeOptions[0])
              }
              isClearable={false}
              placeholder="Date Range"
              width="150px"
            />

            {selectedDateRange.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    param.start_date ? new Date(param.start_date) : null
                  }
                  endDate={param.end_date ? new Date(param.end_date) : null}
                  onChange={([start, end]) => {
                    setParam((prev) => ({
                      ...prev,
                      date_range: "custom",
                      start_date: start ? formatDate(start) : "",
                      end_date: end ? formatDate(end) : "",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}
              <Button variant="link" size="sm" onClick={fetchData}>
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading asset lifecycle history...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table size="sm" className="align-middle text-nowrap small">
                  <thead className="table-light">
                    <tr>
                      <th>Asset</th>
                      <th>Category</th>
                      <th>Event Type</th>
                      <th>Performed By</th>
                      <th>Date</th>
                      <th>Remarks</th>
                      <th className="text-end">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4">
                          <small>
                            No asset lifecycle events found matching your
                            criteria
                          </small>
                        </td>
                      </tr>
                    ) : (
                      reportData.map((log, index) => (
                        <>
                          <tr key={index}>
                            {/* Asset Info */}
                            <td>
                              <div className="fw-semibold">
                                {log.asset?.name || "-"}
                              </div>
                              <div className="small">
                                {log.asset?.asset_number || "-"}
                              </div>
                            </td>

                            {/* Category Info */}
                            <td>
                              <div className="fw-medium">
                                {log.asset?.sub_category?.category?.name || "-"}
                              </div>
                              <div className="small">
                                {log.asset?.sub_category?.name || "-"}
                              </div>
                            </td>

                            {/* Event Type */}
                            <td>
                              <Badge
                                className={`text-capitalize d-inline-flex align-items-center gap-1 bg-${getEventBadge(
                                  log.log_type
                                )}`}
                              >
                                {getEventIcon(log.log_type)}
                                {formatEventType(log.log_type)}
                              </Badge>
                            </td>

                            {/* Performed By */}
                            <td>
                              <div>{log.performed_by?.fullname || "-"}</div>
                              {log.performed_by?.email && (
                                <div className="small">
                                  {log.performed_by.email}
                                </div>
                              )}
                            </td>

                            {/* Date */}
                            <td>{formatDate(log.created_at)}</td>

                            {/* Remarks */}
                            <td>{log.remarks || "-"}</td>

                            {/* Expand Arrow */}
                            <td className="text-end">
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => toggleRow(index)}
                              >
                                {expandedRows.includes(index) ? (
                                  <FaArrowDownLong />
                                ) : (
                                  <FaArrowRight />
                                )}
                              </Button>
                            </td>
                          </tr>

                          {/* Expandable Details Row */}
                          {expandedRows.includes(index) && (
                            <tr>
                              <td colSpan="7">
                                <div className="p-3 small ">
                                  <div className="row gy-2 ">
                                    {log.allocation_log && (
                                      <div className="col-12">
                                        <div className="p-2 bg-light rounded shadow-sm">
                                          <strong>Allocation Details</strong>
                                          <div className="row">
                                            <div className="col-md-3">
                                              <small>Type:</small>{" "}
                                              <div>
                                                {log.allocation_log
                                                  .allocation_type || "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>To Employee:</small>{" "}
                                              <div>
                                                {log.allocation_log.to_employee
                                                  ?.fullname || "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>To Dept:</small>{" "}
                                              <div>
                                                {log.allocation_log
                                                  .to_department?.name || "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>To Area:</small>{" "}
                                              <div>
                                                {log.allocation_log.to_area
                                                  ?.name || "-"}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {log.return_log && (
                                      <div className="col-12">
                                        <div className="p-2 bg-light rounded shadow-sm">
                                          <strong>Return Details</strong>
                                          <div className="row">
                                            <div className="col-md-3">
                                              <small>Status:</small>{" "}
                                              <div>
                                                {log.return_log.return_status ||
                                                  "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>Condition:</small>{" "}
                                              <div>
                                                {log.return_log
                                                  .return_condition || "-"}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {log.movement_log && (
                                      <div className="col-12">
                                        <div className="p-2 bg-light rounded shadow-sm">
                                          <strong>Movement Details</strong>
                                          <div className="row">
                                            <div className="col-md-3">
                                              <small>From:</small>{" "}
                                              <div>
                                                {log.movement_log.from_location?.name || log.movement_log.from_employee?.fullname || "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>To:</small>{" "}
                                              <div>
                                                {log.movement_log.to_location?.name || log.movement_log.to_employee?.fullname || "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>Status:</small>{" "}
                                              <div>
                                                {log.movement_log.movement_status || "-"}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {log.maintenance_log && (
                                      <div className="col-12">
                                        <div className="p-2 bg-light rounded shadow-sm">
                                          <strong>Maintenance Details</strong>
                                          <div className="row">
                                            <div className="col-md-3">
                                              <small>Type:</small>{" "}
                                              <div>
                                                {log.maintenance_log
                                                  .maintenance_type || "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>Cost:</small>{" "}
                                              <div>
                                                {log.maintenance_log.cost
                                                  ? `₹${log.maintenance_log.cost.toLocaleString(
                                                      "en-IN"
                                                    )}`
                                                  : "-"}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {log.disposal_log && (
                                      <div className="col-12">
                                        <div className="p-2 bg-light rounded shadow-sm">
                                          <strong>Disposal Details</strong>
                                          <div className="row">
                                            <div className="col-md-3">
                                              <small>Reason:</small>{" "}
                                              <div>
                                                {log.disposal_log
                                                  .disposal_reason || "-"}
                                              </div>
                                            </div>
                                            <div className="col-md-3">
                                              <small>Method:</small>{" "}
                                              <div>
                                                {log.disposal_log
                                                  .disposal_method || "-"}
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <Pagination
                currentPage={param.page}
                recordsPage={param.page_size}
                dataLength={totalCount}
                handlePagination={handlePageChange}
                showPageInfo
                disabled={loading}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AssetLifecycleHistoryReport;
